import React from "react";
import { motion } from "framer-motion";
import { ArrowDown } from "@phosphor-icons/react";
import { useLanguage } from "@/context/LanguageContext";

export default function HeroSection() {
  const { t } = useLanguage();

  return (
    <section
      id="inicio"
      className="relative min-h-screen flex items-center justify-start overflow-hidden"
      data-testid="hero-section"
    >
      <div className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1599696848652-f0ff23bc911f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBpbnRlcmlvcnxlbnwwfHx8fDE3NzYyMDc4NjB8MA&ixlib=rb-4.1.0&q=85"
          alt="Luxury interior"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 md:py-0 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.6, rotate: -8 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex-shrink-0"
          >
            <motion.div
              animate={{
                filter: [
                  "drop-shadow(0 0 20px rgba(255,215,0,0.2))",
                  "drop-shadow(0 0 40px rgba(255,215,0,0.4))",
                  "drop-shadow(0 0 20px rgba(255,215,0,0.2))"
                ]
              }}
              transition={{ repeat: Infinity, duration: 3, ease: "easeInOut" }}
            >
              <img
                src="/logo.png"
                alt="Dominican Upholstered Logo"
                className="w-52 h-52 sm:w-64 sm:h-64 lg:w-80 lg:h-80 object-contain"
                data-testid="hero-logo"
              />
            </motion.div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
            className="max-w-2xl text-center lg:text-left"
          >
            <p className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#FFD700] mb-6 font-light" data-testid="hero-overline">
              {t.hero.overline}
            </p>

            <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-7xl font-light text-white leading-[0.95] tracking-tighter mb-8" data-testid="hero-title">
              {t.hero.title1}
              <br />
              <span className="text-[#FFD700]">{t.hero.title2}</span> {t.hero.title3 && <>{t.hero.title3.startsWith("&") || t.hero.title3.startsWith("y") ? "" : <br />}{t.hero.title3}</>}
            </h1>

            <p className="text-base md:text-lg font-light text-zinc-300 leading-relaxed mb-12 max-w-lg mx-auto lg:mx-0" data-testid="hero-description">
              {t.hero.description}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <a
                href="#cotizacion"
                className="inline-flex items-center justify-center px-8 py-4 bg-[#FFD700] text-black font-medium uppercase tracking-wider text-sm hover:bg-[#E5C100] transition-all duration-300"
                data-testid="hero-cta-quote"
              >
                {t.hero.ctaQuote}
              </a>
              <a
                href="#galeria"
                className="inline-flex items-center justify-center px-8 py-4 border border-[#C0C0C0]/40 text-[#C0C0C0] font-light uppercase tracking-wider text-sm hover:border-[#FFD700] hover:text-[#FFD700] transition-all duration-300"
                data-testid="hero-cta-gallery"
              >
                {t.hero.ctaGallery}
              </a>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, 12, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-[#FFD700]"
      >
        <ArrowDown size={24} weight="bold" />
      </motion.div>
    </section>
  );
}
